import type { TranslationKey } from "@privacy/translations";
import { useTranslation } from "@privacy/translations";
import type { GridColDef, GridFilterInputValueProps } from "@mui/x-data-grid-premium";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { DynamicGrid } from "../../dynamicGrid";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";
import { usePrivacyDocumentTitle } from "../../shared/usePrivacyDocumentTitle";
import { PartnerIcon2 } from "../../shared/icons";
import { EmptyState } from "../../shared/EmptyState";
import type { Evaluation, GetPartnersGridViewResponse, PartnerRole } from "../../api/partners";
import {
  EvaluationSchema,
  PARTNER_ROLES,
  partnerByIdQueryOptions,
  partnerFormQueryOptions,
  partnersGridViewQueryOptions,
} from "../../api/partners";
import { PageTitle } from "../../shared/Page";
import { HasPermission } from "../../shared/permissions";
import { Grid } from "../../shared/Grid";
import { EmptyCell } from "../../dynamicGrid/columnDefinitions";
import { CreatePartnerDialog } from "./CreatePartnerDialog";
import { ROLES_INFO } from "./constants";
import { PartnerMainDrawer } from "./PartnerMainDrawer";
import { PartnerActionMenu } from "./PartnerDrawerHeader";
import {
  CONNECTED_ENTITIES_DRAWER_PATH_SUFFIX,
  PartnerSubDrawerConnectedEntities,
} from "./PartnerRoleSubDrawerConnectedEntities";
import { ComplianceManuelHistoryDrawer } from "./ComplianceManuelHistoryDrawer";
import { ComplianceSubDrawer, EvaluationDot } from "./ComplianceSubDrawer";
import { PartnerRoleSubdrawer } from "./PartnerRoleSubdrawer";
import { PartnerLifecycleStatusChip } from "./PartnerLifecycleStatusChip";
import { prefetch } from "../../api/prefetch";
import { useQuery } from "@tanstack/react-query";

const PartnersGridPage = () => {
  usePrivacyDocumentTitle("Sidebar.Partners");
  const { t } = useTranslation();
  const partnersGridViewQuery = useQuery(partnersGridViewQueryOptions());
  const partnerFormQuery = useQuery(partnerFormQueryOptions());
  const { navigate, paths } = usePrivacyNavigate();

  return (
    <DynamicGrid.Grid
      rowsData={partnersGridViewQuery.data?.partners}
      form={partnerFormQuery.data}
      getRowId={getRowId}
      getColumns={questionCardColumns => [
        statusColumn(t),
        roleColumn({ headerName: t("PartnersGrid.RoleHeader"), roleToLabel: role => t(ROLES_INFO[role].labelKey) }),
        complianceColumn(t),
        ...questionCardColumns,
      ]}
      mainColumnProps={{
        headerName: t("PartnersGrid.NameHeader"),
        renderLabel: row => row.name,
        renderIcon: ({ iconProps }) => <PartnerIcon2 {...iconProps} />,
      }}
      toolbarTabs={{
        queryKey: "status",
        filter: (row, activeTab) => (activeTab === "all" ? true : row.status === activeTab),
        tabs: [
          { labelKey: "PartnersGrid.FilterAll", value: "all" },
          { labelKey: "PartnersGrid.FilterActive", value: "active" },
          { labelKey: "PartnersGrid.FilterInactive", value: "inactive" },
          { labelKey: "PartnersGrid.FilterArchived", value: "archived" },
        ],
      }}
      onRowSelect={id => navigate(paths.partnersInline(id))}
      Toolbar={PartnersGridToolbar}
      Footer={PartnersGridFooter}
      NoRowsOverlay={NoPartnersOverlay}
      texts={{ searchFieldPlaceholder: t("PartnersGrid.SearchFieldLabel") }}
      renderActionMenu={({ row, selectedRowId }) => (
        <PartnerActionMenu
          partnerName={row.name}
          partnerId={row.id}
          partnerStatus={row.status}
          onDeleted={() => {
            if (selectedRowId === row.id) navigate(paths.partnersGrid());
          }}
        />
      )}
    />
  );
};

type PartnerGridPageRow = GetPartnersGridViewResponse["partners"][number];
const getRowId = (row: PartnerGridPageRow) => row.id;

const statusColumn = (t: (translation: TranslationKey) => string): GridColDef<PartnerGridPageRow> => {
  return {
    ...DynamicGrid.requiredColDef({ headerName: t("PartnersGrid.StatusHeader"), field: "status" }),
    width: 130,
    filterOperators: [
      {
        value: "is",
        getApplyFilterFn: filterItem => {
          if (!filterItem.field || !filterItem.value || !filterItem.operator) return null;
          return value => value === filterItem.value;
        },
        InputComponent: StatusColumnFilterInput,
      },
    ],
    /** The value getter value is used for sorting and searching */
    valueGetter: (_, row) => t(`PartnerLifecycleStatus.${row.status}`),
    renderCell: ({ row }) => (
      <DynamicGrid.Cell>
        <PartnerLifecycleStatusChip status={row.status} />
      </DynamicGrid.Cell>
    ),
  };
};

const StatusColumnFilterInput = (props: GridFilterInputValueProps) => {
  const { t } = useTranslation();
  const { item, applyValue, focusElementRef } = props;

  return (
    <Select
      fullWidth
      size="small"
      value={item.value ?? ""}
      onChange={event => applyValue({ ...item, value: event.target.value })}
      inputRef={focusElementRef}
    >
      <MenuItem hidden sx={{ display: "none" }} value="">
        {t("Filter.DefaultValue")}
      </MenuItem>
      <MenuItem value={t("PartnerLifecycleStatus.active")}>{t("PartnerLifecycleStatus.active")}</MenuItem>
      <MenuItem value={t("PartnerLifecycleStatus.inactive")}>{t("PartnerLifecycleStatus.inactive")}</MenuItem>
      <MenuItem value={t("PartnerLifecycleStatus.archived")}>{t("PartnerLifecycleStatus.archived")}</MenuItem>
    </Select>
  );
};

const PartnersGridToolbar = () => {
  const { t } = useTranslation();
  const { navigate, paths } = usePrivacyNavigate();
  return (
    <DynamicGrid.Toolbar
      pageTitle={<PageTitle title={t("PartnersGrid.Title")} subTitle={t("PartnersGrid.SubTitle")} />}
      actionButtons={
        <>
          <DynamicGrid.ExportButton />

          <HasPermission permission="nova-forms-api:partner:create">
            <DynamicGrid.CreateNewButton
              labelKey="CreatePartner.Button"
              renderDialog={({ onClose }) => (
                <CreatePartnerDialog
                  onClose={onClose}
                  onCreated={partner => navigate(paths.partnersInline(partner.id))}
                />
              )}
            />
          </HasPermission>
        </>
      }
      filterButtons={
        <>
          <DynamicGrid.ColumnFilterButton />
          <DynamicGrid.FilterButton />
        </>
      }
    />
  );
};

const PartnersGridFooter = () => <DynamicGrid.Footer numberOfRowsTextKey="Partners.Grid.NumberOfRows" />;

const NoPartnersOverlay = () => <EmptyState labelKey="Partners.NoRows" />;

const complianceColumn = (t: (translation: TranslationKey) => string): GridColDef<PartnerGridPageRow> => {
  return {
    ...DynamicGrid.requiredColDef({ headerName: t("PartnersGrid.ComplianceHeader"), field: "compliance" }),
    width: 250,
    filterOperators: [
      {
        value: "is",
        getApplyFilterFn: filterItem => {
          if (!filterItem.field || !filterItem.value || !filterItem.operator) return null;
          return value => value === filterItem.value;
        },
        InputComponent: ComplianceColumnFilterInput,
      },
    ],
    valueGetter: (_, row) =>
      row.compliance.overallAssessment ? t(`Compliance.Subdrawer.Evaluation.${row.compliance.overallAssessment}`) : "",
    renderCell: ({ row }) => {
      const assessment = row.compliance.overallAssessment;
      if (!assessment) return <EmptyCell />;
      return (
        <DynamicGrid.Cell>
          <Stack direction="row" sx={{ alignItems: "center", gap: 0.5 }}>
            <EvaluationDot evaluation={assessment} />
            <Typography sx={{ fontSize: theme => theme.typography.pxToRem(14), fontWeight: 600, lineHeight: 1 }}>
              {t(`Compliance.Subdrawer.Evaluation.${assessment}`)}
            </Typography>
          </Stack>
        </DynamicGrid.Cell>
      );
    },
  };
};

const ComplianceColumnFilterInput = (props: GridFilterInputValueProps) => {
  const { t } = useTranslation();
  const { item, applyValue, focusElementRef } = props;

  return (
    <Select
      fullWidth
      size="small"
      value={item.value ?? ""}
      onChange={event => applyValue({ ...item, value: event.target.value })}
      inputRef={focusElementRef}
    >
      <MenuItem hidden sx={{ display: "none" }} value="">
        {t("Filter.DefaultValue")}
      </MenuItem>
      {EvaluationSchema.options.map((option: Evaluation) => (
        <MenuItem key={option} value={t(`Compliance.Subdrawer.Evaluation.${option}`)}>
          <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
            <EvaluationDot evaluation={option} />
            {t(`Compliance.Subdrawer.Evaluation.${option}`)}
          </Stack>
        </MenuItem>
      ))}
    </Select>
  );
};

const roleColumn = ({
  headerName,
  roleToLabel,
}: {
  headerName: string;
  roleToLabel: (role: PartnerRole) => string;
}): GridColDef<GetPartnersGridViewResponse["partners"][number]> => ({
  ...DynamicGrid.requiredColDef({ headerName, field: "roles" }),
  width: 330,
  valueGetter: (_, row) => row.roles.map(roleToLabel).join(Grid.VALUE_GETTER_DELIMITER),
  renderCell: ({ row }) => {
    if (row.roles.length === 0) return <EmptyCell />;

    return (
      <DynamicGrid.Cell withExtraSpacingTop={false}>
        <DynamicGrid.LimitVisibleItems
          items={row.roles}
          numberOfVisibleItems={2}
          renderItem={(role: PartnerRole) => <DynamicGrid.Chip variant="choice" label={roleToLabel(role)} key={role} />}
        />
      </DynamicGrid.Cell>
    );
  },
});

export const partnersGridPageRoutes = DynamicGrid.setupRoutes({
  gridElement: <PartnersGridPage />,
  rowDrawerRoutes: [
    { index: true, element: <PartnerMainDrawer /> },
    ...PARTNER_ROLES.map(role => ({
      path: role,
      element: <PartnerRoleSubdrawer role={role} />,
    })),
    ...PARTNER_ROLES.map(role => ({
      path: `${role}/${CONNECTED_ENTITIES_DRAWER_PATH_SUFFIX}`,
      element: <PartnerSubDrawerConnectedEntities role={role} />,
    })),
    { path: "compliance", element: <ComplianceSubDrawer /> },
    { path: "compliance/manual-history", element: <ComplianceManuelHistoryDrawer /> },
  ],
  gridPrefetcher: () => void prefetchPartnersGridViewAndForm(),
  rowDrawerDefaultPrefetcher: ({ rowId }) => void prefetch(partnerByIdQueryOptions(rowId)),
});

export const prefetchPartnersGridViewAndForm = () => {
  prefetch(partnersGridViewQueryOptions());
  prefetch(partnerFormQueryOptions());
};
